import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	DEFAULT_DATE_TIME_FORMAT,
	FormattedDateTime,
	ImageWithAspectRatio,
	InfoIconTooltipPopover,
	SlateViewer,
	isEmptySlateValue,
	useIsAddonEnabled,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Address } from "../../../../shared/ui/Address";
import { DetailsPageInfoField } from "../../../../shared/ui/DetailsPageInfoField";
import { EventDetailResource } from "../../shared/types";
import { EventContextMenu } from "./EventContextMenu";

interface Props {
	event: EventDetailResource;
	isArchived: boolean;
}

const GridCardBody = styled(CardBody)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: ${({ theme }) => theme.spacing(2)};
`;

export const EventOverviewCard: FC<Props> = ({ event, isArchived }) => {
	const { t } = useTranslation();
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");

	return (
		<Card>
			<ImageWithAspectRatio image={event.image} alt={event.image.name} />
			<CardHeader>
				<CardHeading>{t("Overview")}</CardHeading>
				{!isArchived && (
					<CardHeaderActions>
						<EventContextMenu event={event} />
					</CardHeaderActions>
				)}
			</CardHeader>
			<GridCardBody>
				<DetailsPageInfoField title={t("Name")} value={event.name} />
				<DetailsPageInfoField title={t("Event type")} value={event.eventType.name} />
				<DetailsPageInfoField
					title={t("Start")}
					value={
						<FormattedDateTime
							dateTime={event.start}
							format={{ ...DEFAULT_DATE_TIME_FORMAT, timeZone: event.timezone }}
						/>
					}
				/>
				<DetailsPageInfoField
					title={t("End")}
					value={
						<FormattedDateTime
							dateTime={event.end}
							format={{ ...DEFAULT_DATE_TIME_FORMAT, timeZone: event.timezone }}
						/>
					}
				/>
				<DetailsPageInfoField title={t("Timezone")} value={event.timezone} />
				<DetailsPageInfoField
					title={t("Primary language")}
					value={event.primaryLanguage ? event.primaryLanguage.name : "-"}
				/>
				{event.eventType.eventLanguageRequirement !== "prohibited" && (
					<DetailsPageInfoField
						title={t("Event languages")}
						value={event.languages.length > 0 ? event.languages.map((language) => language.name).join(", ") : "-"}
					/>
				)}
				{event.eventType.organizerRequirement !== "prohibited" && (
					<DetailsPageInfoField
						title={t("Organizer")}
						value={
							event.organizer ? (
								<>
									{event.organizer.name}{" "}
									{event.organizer.description && !isEmptySlateValue(event.organizer.description) && (
										<InfoIconTooltipPopover>
											<SlateViewer content={event.organizer.description} />
										</InfoIconTooltipPopover>
									)}
								</>
							) : (
								"-"
							)
						}
					/>
				)}
				<DetailsPageInfoField title={t("Creator")} value={event.creator ? event.creator.name : "-"} />
				<DetailsPageInfoField title={t("Visibility")} value={t(`eventVisibility.${event.visibility}`)} />
				{isPressModuleEnabled && <DetailsPageInfoField title={t("Clippings")} value={event.clippingCount || 0} />}
				{event.eventType.locationRequirement !== "prohibited" && (
					<DetailsPageInfoField title={t("Location")} value={event.location?.name || "-"} />
				)}
				{event.eventType.addressRequirement !== "prohibited" && (
					<DetailsPageInfoField
						title={t("Address")}
						value={event.address ? <Address address={event.address} /> : "-"}
					/>
				)}
				{event.eventType.eventGoalRatingMetricRequirement !== "prohibited" && (
					<DetailsPageInfoField title={t("Rating")} value={event.rating ?? "-"} />
				)}
			</GridCardBody>
		</Card>
	);
};
